import { RSVPParty, PartyMember, RSVPFormData } from '../types'
import { fetchPartiesFromSheets, updatePartyRSVPInSheets } from '../services/googleSheets'

/**
 * RSVP parties with multiple members
 * This data is now dynamically loaded from Google Sheets
 * Static data kept as fallback for development
 */
export const staticRsvpParties: RSVPParty[] = [
  {
    id: 'party-1',
    partyName: 'The Smith Family',
    primaryContact: {
      name: 'John Smith',
      email: 'john.smith@email.com',
      phone: '555-0123'
    },
    members: [
      {
        id: 'member-1-1',
        name: 'John Smith',
        email: 'john.smith@email.com',
        type: 'primary',
        age: 'adult'
      },
      {
        id: 'member-1-2',
        name: 'Jane Smith',
        type: 'spouse',
        age: 'adult'
      },
      {
        id: 'member-1-3',
        name: 'Emma Smith',
        type: 'child',
        age: 'child'
      },
      {
        id: 'member-1-4',
        name: 'Lucas Smith',
        type: 'child',
        age: 'child'
      }
    ],
    maxPartySize: 5, // Family of 4 + 1 plus one allowed
    allowsAdditionalPlusOnes: true,
    hasRSVPed: false
  },
  {
    id: 'party-2',
    partyName: 'Sarah Johnson',
    primaryContact: {
      name: 'Sarah Johnson',
      email: 'sarah.johnson@email.com'
    },
    members: [
      {
        id: 'member-2-1',
        name: 'Sarah Johnson',
        email: 'sarah.johnson@email.com',
        type: 'primary',
        age: 'adult'
      }
    ],
    maxPartySize: 2, // Sarah + 1 plus one
    allowsAdditionalPlusOnes: true,
    hasRSVPed: false
  },
  {
    id: 'party-3',
    partyName: 'The Wilson Family',
    primaryContact: {
      name: 'David Wilson',
      email: 'david.wilson@email.com'
    },
    members: [
      {
        id: 'member-3-1',
        name: 'David Wilson',
        email: 'david.wilson@email.com',
        type: 'primary',
        age: 'adult'
      },
      {
        id: 'member-3-2',
        name: 'Lisa Wilson',
        type: 'spouse',
        age: 'adult'
      },
      {
        id: 'member-3-3',
        name: 'Michael Wilson',
        type: 'child',
        age: 'child'
      }
    ],
    maxPartySize: 3, // No additional plus ones allowed
    allowsAdditionalPlusOnes: false,
    hasRSVPed: false
  },
  {
    id: 'party-4',
    partyName: 'The Brown Family',
    primaryContact: {
      name: 'Michael Brown',
      email: 'michael.brown@email.com'
    },
    members: [
      {
        id: 'member-4-1',
        name: 'Michael Brown',
        email: 'michael.brown@email.com',
        type: 'primary',
        age: 'adult'
      },
      {
        id: 'member-4-2',
        name: 'Rachel Brown',
        type: 'spouse',
        age: 'adult'
      }
    ],
    maxPartySize: 4, // Couple + 2 plus ones
    allowsAdditionalPlusOnes: true,
    hasRSVPed: false
  },
  {
    id: 'party-5',
    partyName: 'Emily Davis',
    primaryContact: {
      name: 'Emily Davis',
      email: 'emily.davis@email.com'
    },
    members: [
      {
        id: 'member-5-1',
        name: 'Emily Davis',
        email: 'emily.davis@email.com',
        type: 'primary',
        age: 'adult'
      }
    ],
    maxPartySize: 1, // No plus ones allowed
    allowsAdditionalPlusOnes: false,
    hasRSVPed: false
  },
  {
    id: 'party-6',
    partyName: 'The Martinez Family',
    primaryContact: {
      name: 'Jessica Martinez',
      email: 'jessica.martinez@email.com'
    },
    members: [
      {
        id: 'member-6-1',
        name: 'Jessica Martinez',
        email: 'jessica.martinez@email.com',
        type: 'primary',
        age: 'adult'
      },
      {
        id: 'member-6-2',
        name: 'Carlos Martinez',
        type: 'spouse',
        age: 'adult'
      },
      {
        id: 'member-6-3',
        name: 'Sofia Martinez',
        type: 'child',
        age: 'infant'
      }
    ],
    maxPartySize: 3, // No additional plus ones
    allowsAdditionalPlusOnes: false,
    hasRSVPed: false
  },
  {
    id: 'party-7',
    partyName: 'The Thompson Family',
    primaryContact: {
      name: 'Robert Thompson',
      email: 'robert.thompson@email.com',
      phone: '555-0789'
    },
    members: [
      {
        id: 'member-7-1',
        name: 'Robert Thompson',
        email: 'robert.thompson@email.com',
        type: 'primary',
        age: 'adult'
      },
      {
        id: 'member-7-2',
        name: 'Linda Thompson',
        type: 'spouse',
        age: 'adult'
      },
      {
        id: 'member-7-3',
        name: 'Alex Thompson',
        type: 'child',
        age: 'child'
      },
      {
        id: 'member-7-4',
        name: 'Maya Thompson',
        type: 'child',
        age: 'child'
      },
      {
        id: 'member-7-5',
        name: 'Sam Thompson',
        type: 'child',
        age: 'child'
      }
    ],
    maxPartySize: 5, // Family of 5, no plus ones allowed
    allowsAdditionalPlusOnes: false,
    hasRSVPed: false
  },
  {
    id: 'party-8',
    partyName: 'The Garcia Family',
    primaryContact: {
      name: 'Maria Garcia',
      email: 'maria.garcia@email.com',
      phone: '555-0456'
    },
    members: [
      {
        id: 'member-8-1',
        name: 'Maria Garcia',
        email: 'maria.garcia@email.com',
        type: 'primary',
        age: 'adult'
      },
      {
        id: 'member-8-2',
        name: 'Diego Garcia',
        type: 'child',
        age: 'child'
      },
      {
        id: 'member-8-3',
        name: 'Isabella Garcia',
        type: 'child',
        age: 'child'
      },
      {
        id: 'member-8-4',
        name: 'Carlos Garcia',
        type: 'child',
        age: 'child'
      },
      {
        id: 'member-8-5',
        name: 'Lucia Garcia',
        type: 'child',
        age: 'child'
      }
    ],
    maxPartySize: 5, // Single parent + 4 children, no plus ones allowed
    allowsAdditionalPlusOnes: false,
    hasRSVPed: false
  }
]

// Dynamic data management with Google Sheets integration
let cachedParties: RSVPParty[] = []
let cachedSearchableNames: Array<{
  name: string
  partyName: string
  partyId: string
  memberId: string
}> = []
let lastFetchTime = 0
let lastNamesFetchTime = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

// Track ongoing fetch promises to prevent duplicate requests
let ongoingPartiesFetch: Promise<RSVPParty[]> | null = null
let ongoingNamesFetch: Promise<Array<{
  name: string
  partyName: string
  partyId: string
  memberId: string
}>> | null = null

/**
 * Get RSVP parties from Google Sheets
 */
export async function getRSVPParties(): Promise<RSVPParty[]> {
  const now = Date.now()
  
  // Return cached data if still fresh
  if (cachedParties.length > 0 && now - lastFetchTime < CACHE_DURATION) {
    return cachedParties
  }
  
  // If there's already an ongoing fetch, return that promise
  if (ongoingPartiesFetch) {
    return ongoingPartiesFetch
  }
  
  try {
    // Start the fetch and store the promise
    ongoingPartiesFetch = fetchPartiesFromSheets()
    cachedParties = await ongoingPartiesFetch
    lastFetchTime = now
    
    // Clear the ongoing fetch promise
    ongoingPartiesFetch = null
    
    return cachedParties
  } catch (error) {
    // Clear the ongoing fetch promise on error
    ongoingPartiesFetch = null
    console.error('Failed to fetch parties from Google Sheets:', error)
    throw error
  }
}

/**
 * Find party by any member's name (case-insensitive)
 * This allows children or spouses to look up the family RSVP
 */
export async function findPartyByMemberName(name: string): Promise<RSVPParty | undefined> {
  const searchName = name.toLowerCase().trim()
  const parties = await getRSVPParties()
  
  return parties.find(party => 
    party.members.some(member => 
      member.name.toLowerCase().trim() === searchName
    )
  )
}

/**
 * Find specific member within a party
 */
export function findMemberInParty(party: RSVPParty, memberName: string): PartyMember | undefined {
  const searchName = memberName.toLowerCase().trim()
  return party.members.find(member => 
    member.name.toLowerCase().trim() === searchName
  )
}

/**
 * Update party RSVP status and member details
 */
export async function updatePartyRSVP(partyId: string, formData: RSVPFormData): Promise<boolean> {
  try {
    const parties = await getRSVPParties()
    const party = parties.find(p => p.id === partyId)
    
    if (!party) return false
    
    // Update in Google Sheets
    await updatePartyRSVPInSheets(party, formData)
    
    // Update local cache with complete party data
    const partyIndex = cachedParties.findIndex(p => p.id === partyId)
    if (partyIndex !== -1) {
      // Update existing members with form data (excluding plus ones)
      const originalMembers = party.members.filter(member => member.type !== 'plus-one')
      const updatedMembers = originalMembers.map(member => {
        const formMember = formData.partyMembers.find(fm => fm.id === member.id)
        const isAttending = formMember?.isAttending ?? false
        return {
          ...member,
          isAttending,
          isAttendingWelcomeDrinks: formMember?.isAttendingWelcomeDrinks ?? false,
          mealChoice: isAttending ? (formMember?.mealChoice as PartyMember['mealChoice'] || undefined) : undefined,
          notes: formMember?.notes || undefined
        }
      })

      // Add plus ones as new members (replacing any existing plus ones)
      const newPlusOnes = formData.additionalPlusOnes?.map((plusOne, index) => ({
        id: `plus-one-${partyId}-${Date.now()}-${index}`,
        name: plusOne.name,
        type: 'plus-one' as const,
        age: 'adult' as const,
        isAttending: true,
        isAttendingWelcomeDrinks: plusOne.isAttendingWelcomeDrinks ?? false,
        mealChoice: plusOne.mealChoice as PartyMember['mealChoice'] || undefined,
        notes: plusOne.notes || undefined
      })) || []

      cachedParties[partyIndex] = {
        ...cachedParties[partyIndex],
        hasRSVPed: true,
        rsvpSubmittedAt: new Date(),
        rsvpSubmittedBy: formData.searchName,
        specialRequests: formData.specialRequests || '',
        members: [...updatedMembers, ...newPlusOnes]
      }
    }
    
    // Force cache refresh to ensure consistency
    setTimeout(() => {
      refreshPartyData()
    }, 1000) // Refresh after 1 second to allow Google Sheets to update
    
    return true
  } catch (error) {
    console.error('Error updating party RSVP:', error)
    return false
  }
}

/**
 * Get remaining plus one slots for a party
 */
export function getRemainingPlusOneSlots(party: RSVPParty): number {
  if (!party.allowsAdditionalPlusOnes) return 0
  return Math.max(0, party.maxPartySize - party.members.length)
}

/**
 * Add additional plus one to party (handled via RSVP form submission)
 */
export function addPlusOneToParty(_partyId: string, _plusOneName: string): boolean {
  // This is now handled through the RSVP form submission process
  // which will update Google Sheets directly
  console.warn('addPlusOneToParty is deprecated. Use RSVP form submission instead.')
  return false
}

/**
 * Refresh cached data (force reload from Google Sheets)
 */
export async function refreshPartyData(): Promise<RSVPParty[]> {
  console.log('Refreshing party data from Google Sheets...')
  // Clear all caches
  cachedParties = []
  cachedSearchableNames = []
  lastFetchTime = 0
  lastNamesFetchTime = 0
  
  // Cancel any ongoing fetches
  ongoingPartiesFetch = null
  ongoingNamesFetch = null
  
  return await getRSVPParties()
}

/**
 * Get all searchable names for autocomplete
 */
export async function getAllSearchableNames(): Promise<Array<{
  name: string
  partyName: string
  partyId: string
  memberId: string
}>> {
  const now = Date.now()
  
  // Return cached names if still fresh
  if (cachedSearchableNames.length > 0 && now - lastNamesFetchTime < CACHE_DURATION) {
    return cachedSearchableNames
  }
  
  // If there's already an ongoing fetch, return that promise
  if (ongoingNamesFetch) {
    return ongoingNamesFetch
  }
  
  try {
    // Start the fetch and store the promise
    ongoingNamesFetch = (async () => {
      const parties = await getRSVPParties()
      const names: Array<{
        name: string
        partyName: string
        partyId: string
        memberId: string
      }> = []
      
      parties.forEach(party => {
        party.members.forEach(member => {
          names.push({
            name: member.name,
            partyName: party.partyName,
            partyId: party.id,
            memberId: member.id
          })
        })
      })
      
      return names
    })()
    
    cachedSearchableNames = await ongoingNamesFetch
    lastNamesFetchTime = now
    
    // Clear the ongoing fetch promise
    ongoingNamesFetch = null
    
    return cachedSearchableNames
  } catch (error) {
    // Clear the ongoing fetch promise on error
    ongoingNamesFetch = null
    throw error
  }
} 